import React, { useState, useRef } from "react";
import { Link, Navigate, useParams } from 'react-router-dom';
import { ChangePassword, isAuthenticated } from "../includes/auth";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import { MdLock, MdArrowForward, MdCancel, MdCheckCircle } from "react-icons/md";
import { BiWorld } from "react-icons/bi";
import logoelakayDiamant from '../includes/Layout/icon/logoelakayDiamant-256.png';
import './user.css';
import AuthPageNav from '../includes/auth/AuthPageNav';
import { elakayAutoText } from "../includes/pageInside/dataElakay";
import { displayAuthError } from "../includes/auth/authErrors";


let __elakayInfoSource;
const __setElakayInfoSource = (infoSource) => { if (infoSource) __elakayInfoSource = infoSource; };
const __elakayText = (key, fallback) =>
  __elakayInfoSource?.laguangeElakay?.("frontElakayHardcodedText", __elakayInfoSource.languageAPI)?.["password"]?.[key] || fallback;

const UserPasswordChange = props => {
    const { 
        infoSource,
        
        // === HEADER ANIMATIONS ===
        enableHeaderAnimations = false,
        enableHeaderShapes = false,
        enableLogoAnimation = false,
        enableLogoPulse = false,
        enableLogoHover = false,
        enableDecorationAnimations = false,
        
        // === PARTICLES ===
        enableParticles = false,
        particlesCount = 0,
        
        // === CARD ===
        enableCardEntrance = false,
        enableCardGlow = false,
        
        // === FORM HEADER / ICON ===
        enableTitleGlow = false,
        enableIconAnimation = false,
        
        // === FORM INPUTS ===
        enableFormGroupFade = false,
        enableInputFocus = false,
        enablePasswordToggle = true,
        enablePasswordStrength = true,
        enablePasswordMatch = true,
        
        // === BUTTONS ===
        enableButtonHover = false,
        enableButtonIcon = false,
        
        // === LINKS ===
        enableLinkHover = false,
        
        // === ALERTS ===
        enableAlertSlide = false,
        enableLoadingDots = false,
        enableSuccessAlert = true,
        enableSuccessIcon = true,
        
        // === FOOTER ===
        enableFooterWaves = false,
        enableFooterLinkHover = false
    } = props;
  __setElakayInfoSource(infoSource);
    
    let params = useParams();
    
    const [showPassword, setShowPassword] = useState(true);
    const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(true);
    const [values, setValues] = useState({
        password: "",
        passwordConfirmation: "",
        error: "",
        errorCode: null,
        success: false,
        token: params.token,
        loading: false,
        redirectToReferrer: false
    });

    const passwordRef = useRef(null);
    const passwordConfirmationRef = useRef(null);
    const scrollToError = (ref) => {
        if (ref && ref.current) {
            ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
            ref.current.focus?.();
        }
    };

    if (isAuthenticated()) {
        return <Navigate to="/" />;
    }

    const { password, passwordConfirmation, token, loading, error, success, redirectToReferrer } = values;

    const handleChange = name => event => {
        setValues({ ...values, error: false, errorCode: null, [name]: event.target.value });
    };

    const getPasswordStrength = () => {
        if (password.length === 0) return '';
        if (password.length < 6) return 'weak';
        if (password.length < 10) return 'medium';
        return 'strong';
    };

    const getPasswordStrengthText = () => {
        if (password.length === 0) return '';
        if (password.length < 6) return __elakayText("t013", "Weak");
        if (password.length < 10) return __elakayText("t014", "Medium");
        return __elakayText("t015", "Strong");
    };

    const clickSubmit = event => {
        event.preventDefault();

        if (!token) {
            setValues({ ...values, error: "", errorCode: "RESET_TOKEN_REQUIRED" });
            return;
        }

        // 7 = the User model's real minimum (see Signup.js).
        if (password.length < 7) {
            setValues({ ...values, error: "", errorCode: "PASSWORD_TOO_SHORT" });
            scrollToError(passwordRef);
            return;
        }

        if (password !== passwordConfirmation) {
            setValues({ ...values, error: "", errorCode: "PASSWORD_CONFIRMATION_MISMATCH" });
            scrollToError(passwordConfirmationRef);
            return;
        }

        setValues({ ...values, loading: true });
        
        ChangePassword(token, { password, passwordConfirmation }).then(data => {
            if (data.error) {
                // The reset link carries the token the auth middleware checks:
                // its generic UNAUTHORIZED means this link is invalid/already used.
                const resetCode = data.code === "UNAUTHORIZED" || data.code === "INVALID_TOKEN" ? "INVALID_RESET_TOKEN" : data.code;
                setValues({ ...values, error: data.error, errorCode: resetCode || null, loading: false });
            } else {
                setValues({
                    ...values,
                    password: "",
                    passwordConfirmation: "",
                    error: "",
                    success: true,
                    loading: false,
                    redirectToReferrer: true
                });
            }
        });
    };

    const clickelakay = () => { 
        return window.location.href = "/";
    };

    // Re-computed on every render: a language switch re-translates an error
    // already displayed (errorCode + active language), no new request.
    const shownError = displayAuthError(values.errorCode, error);

    const showError = () => (
        <div
            className={`sign-alert sign-alert-danger ${!enableAlertSlide ? 'no-slide' : ''}`}
            style={{ display: shownError ? "" : "none" }}
            role="alert"
        >
            {shownError}
        </div>
    );

    const showLoading = () =>
        loading && (
            <div className={`sign-alert sign-alert-loading ${!enableAlertSlide ? 'no-slide' : ''}`}>
                {enableLoadingDots && (
                    <div className="sign-loading-dots">
                        <div className="sign-dot"></div>
                        <div className="sign-dot"></div>
                        <div className="sign-dot"></div>
                    </div>
                )}
                <span className="sign-loading-text">
                    {infoSource.language(infoSource, "title", "loanding") || elakayAutoText("h_b04ba49f848624", "Loading...")}
                </span>
            </div>
        );

    const showSuccess = () =>
        success && enableSuccessAlert && (
            <div className={`sign-alert sign-alert-success ${!enableAlertSlide ? 'no-slide' : ''}`}>
                {enableSuccessIcon && (
                    <MdCheckCircle className="sign-alert-icon" />
                )}
                <div className="sign-alert-content">
                    <p className="sign-alert-text">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).password.t001}</p>
                    <p className="sign-alert-subtitle">{infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).password.t002}</p>
                </div>
            </div>
        );

    const signUpForm = () => (
        <div className={`sign-page-wrapper ${!enableHeaderAnimations ? 'no-animations' : ''}`}>
            {/* Animated Header */}
            <header className={`sign-header ${!enableHeaderAnimations ? 'no-header-animation' : ''}`}>
                {enableHeaderShapes && (
                    <div className="sign-header-bg-shapes">
                        <div className="sign-shape sign-shape-1"></div>
                        <div className="sign-shape sign-shape-2"></div>
                        <div className="sign-shape sign-shape-3"></div>
                        <div className="sign-shape sign-shape-4"></div>
                    </div>
                )}
                
                <div className="sign-header-content">
                    <div 
                        className={`sign-logo ${!enableLogoHover ? 'no-hover' : ''}`}
                        onClick={() => clickelakay()}
                    >
                        <div className="sign-logo-icon-wrapper">
                            <img 
                                className={`sign-logo-image ${!enableLogoAnimation ? 'no-bounce' : ''}`} 
                                src={logoelakayDiamant} 
                                width="40" 
                                alt={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).password.t003}
                            />
                            {enableLogoPulse && <div className="sign-logo-pulse"></div>}
                        </div>
                        <span className="sign-logo-text">{__elakayText("t009", "elakay")}</span>
                    </div>
                    
                    <AuthPageNav />
                    {enableDecorationAnimations && (
                        <div className="sign-header-decorations">
                            <BiWorld className="sign-decoration-icon sign-decoration-1" />
                            <div className="sign-decoration-line"></div>
                        </div>
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="sign-main-content">
                {enableParticles && (
                    <div className="sign-particles">
                        {[...Array(particlesCount)].map((_, i) => (
                            <div key={i} className="sign-particle" style={{
                                left: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 3}s`,
                                animationDuration: `${3 + Math.random() * 2}s`
                            }}></div>
                        ))}
                    </div>
                )}

                <div className="sign-form-container">
                    <div className={`sign-form-card ${!enableCardEntrance ? 'no-entrance' : ''}`}>
                        {enableCardGlow && <div className="sign-form-card-glow"></div>}
                        
                        <form className="sign-form" onSubmit={clickSubmit}>
                            {/* Icon Header */}
                            <div className="sign-form-header">
                                <div className={`sign-verification-icon-wrapper ${!enableIconAnimation ? 'no-animation' : ''}`}>
                                    <div className={`sign-verification-icon-circle ${!enableIconAnimation ? 'no-pulse' : ''}`}>
                                        <FaLock className="sign-verification-icon" />
                                    </div>
                                </div>
                                <h1 className={`sign-form-title ${!enableTitleGlow ? 'no-glow' : ''}`}>
                                    {infoSource.laguangeElakay('signinPage', infoSource.languageAPI).title1 || "Change Password"}
                                </h1>
                                <p className="sign-form-subtitle">{__elakayText("t006", "Create a new secure password for your account")}</p>
                            </div>

                            {showLoading()}
                            {showError()}
                            {showSuccess()}

                            {/* ========== NEW PASSWORD ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).Password || "New Password"}
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdLock className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={passwordRef}
                                        onChange={handleChange("password")}
                                        type={showPassword ? "password" : "text"}
                                        value={password}
                                        name="password"
                                        id="inputPassword"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`}
                                        placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).password.t004}
                                        required
                                    />
                                    {enablePasswordToggle && (
                                        <button
                                            type="button"
                                            className="sign-password-toggle"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <FaEye /> : <FaEyeSlash />}
                                        </button>
                                    )}
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_password_hint_new", "At least 7 characters")}</span>

                                {/* Password Strength Meter */}
                                {enablePasswordStrength && (
                                    <div className="sign-password-strength">
                                        <div className="sign-strength-bar">
                                            <div className={`sign-strength-fill ${getPasswordStrength()}`}></div>
                                        </div>
                                        <span className={`sign-strength-text ${getPasswordStrength()}`}>
                                            {getPasswordStrengthText()}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* ========== CONFIRM PASSWORD ========== */}
                            <div className={`sign-form-group ${!enableFormGroupFade ? 'no-fade' : ''}`}>
                                <label className="sign-input-label">
                                    {infoSource.laguangeElakay('label', infoSource.languageAPI).confirmPassword || "Confirm Password"}
                                    <span style={{ color: 'red' }}> *</span>
                                </label>
                                <div className="sign-input-wrapper">
                                    <div className="sign-input-icon-wrapper">
                                        <MdLock className={`sign-input-icon ${!enableInputFocus ? 'no-animation' : ''}`} />
                                    </div>
                                    <input
                                        ref={passwordConfirmationRef}
                                        onChange={handleChange("passwordConfirmation")}
                                        type={showPasswordConfirmation ? "password" : "text"}
                                        value={passwordConfirmation}
                                        name="passwordConfirmation"
                                        id="inputPasswordConfirmation"
                                        className={`sign-form-input ${!enableInputFocus ? 'no-focus-animation' : ''}`} 
                                        placeholder={infoSource.laguangeElakay("frontElakayHardcodedText", infoSource.languageAPI).password.t005}
                                        required  
                                    />
                                    {enablePasswordToggle && (
                                        <button 
                                            type="button"
                                            className="sign-password-toggle" 
                                            onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                                        >
                                            {showPasswordConfirmation ? <FaEye /> : <FaEyeSlash />}
                                        </button>
                                    )}
                                </div>
                                <span className="sign-input-hint">{elakayAutoText("h_password_hint_confirm", "Re-enter the same password")}</span>

                                {/* Password Match Indicator */}
                                {enablePasswordMatch && passwordConfirmation && (
                                    <div style={{ 
                                        marginTop: '8px', 
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: password === passwordConfirmation ? '#34c759' : '#ef4444',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}>
                                        {password === passwordConfirmation ? 
                                            elakayAutoText("h_1362e1643cfc86", "✓ Passwords match") : 
                                            elakayAutoText("h_5507efd81e21d2", "✗ Passwords do not match")
                                        }
                                    </div>
                                )}
                            </div>

                            <input type="hidden" name="verication" placeholder="" />

                            {/* ========== SUBMIT BUTTON ========== */}
                            <button 
                                type="submit"
                                className={`sign-btn-primary sign-btn-full ${enableButtonIcon ? 'sign-btn-animated' : ''} ${!enableButtonHover ? 'no-hover' : ''}`}
                                disabled={loading}
                            >
                                <span>{infoSource.language(infoSource, 'Button', "submit") || elakayAutoText("h_49289db43e663a", "Change Password")}</span>
                                {enableButtonIcon && <MdArrowForward className="sign-btn-icon" />}
                            </button>

                            {/* ========== CANCEL BUTTON ========== */}
                            <Link 
                                className={`sign-btn-secondary sign-btn-full ${!enableButtonHover ? 'no-hover' : ''}`}
                                to="/signin"
                            >
                                {enableButtonIcon && <MdCancel className="sign-btn-icon" />}
                                <span>{infoSource.laguangeElakay('Button', infoSource.languageAPI).cancel || "Cancel"}</span>
                            </Link>
                        </form>
                    </div>
                </div>
            </main>

            {/* Animated Footer */}
            <footer className={`sign-footer ${!enableFooterWaves ? 'no-waves' : ''}`}>
                {enableFooterWaves && (
                    <div className="sign-footer-waves">
                        <div className="sign-wave sign-wave-1"></div>
                        <div className="sign-wave sign-wave-2"></div>
                        <div className="sign-wave sign-wave-3"></div>
                    </div>
                )}
                
                <div className="sign-footer-content">
                    <p className="sign-footer-text">
                        © {new Date().getFullYear()}{__elakayText("t007", "elakay. All rights reserved.")}</p>
                    <div className="sign-footer-links">
                        <Link 
                            to="/privacy" 
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t010", "Privacy")}</Link>
                        <span className="sign-footer-separator">•</span>
                        <Link 
                            to="/terms" 
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t011", "Terms")}</Link>
                        <span className="sign-footer-separator">•</span>
                        <Link 
                            to="/help" 
                            className={`sign-footer-link ${!enableFooterLinkHover ? 'no-hover' : ''}`}
                        >{__elakayText("t012", "Help")}</Link>
                    </div>
                </div>
            </footer>
        </div>
    );

    const redirectUser = () => {
        if (redirectToReferrer) {
            return <Navigate to="/signin" />;
        }
    };

    return (
        <div className="sign-app-wrapper">
            {signUpForm()}
            {redirectUser()}
        </div>
    );
};

export default UserPasswordChange;
